import * as ng from 'angular';

import {
   Dataset,
   ReportsService,
   PieChartFactory,
   MapChartFactory,
   LineChartFactory,
   LineChartCustomFactory,
   FunnelChartFactory,
   GaugeChartFactory,
   BarChartFactory,
   BarChartCustomFactory,
   TableChartFactory,
   PieChartGridFactory,
   NpsPieChartFactory,
   NpsBarChartFactory
} from '..';

// Data can be overwritten and passed into gauge chart types using datacurrent and datamax
// Loads data for defined chart types and loads data via API call to reportsService
export class ReportChartController implements ng.IComponentController {
   dataset: Dataset;
   datacurrent: number;
   datamax: number;
   colors: string[];
   filter: {};

   onFilterChange: {
      (arg: {$field: string, $value: any});
   };

   chart;

   get ds() {
      return this.dataset;
   }

   static $inject = [
      'pieChartFactory',
      'pieChartGridFactory',
      'lineChartFactory',
      'mapChartFactory',
      'funnelChartFactory',
      'gaugeChartFactory',
      'barChartFactory',
      'tableChartFactory',
      'npsPieChartFactory',
      'npsBarChartFactory',
      'lineChartCustomFactory',
      'barChartCustomFactory',
      'ReportsService',
      '$timeout'
   ];

   constructor(
      private pieChartFactory: PieChartFactory,
      private pieChartGridFactory: PieChartGridFactory,
      private lineChartFactory: LineChartFactory,
      private mapChartFactory: MapChartFactory,
      private funnelChartFactory: FunnelChartFactory,
      private gaugeChartFactory: GaugeChartFactory,
      private barChartFactory: BarChartFactory,
      private tableChartFactory: TableChartFactory,
      private npsPieChartFactory: NpsPieChartFactory,
      private npsBarChartFactory: NpsBarChartFactory,
      private lineChartCustomFactory: LineChartCustomFactory,
      private barChartCustomFactory: BarChartCustomFactory,
      private reportsService: ReportsService,
      private $timeout: ng.ITimeoutService
   ) {}

   $onChanges(onChangesObj: ng.IOnChangesObject) {
      let reload = false;
      if (onChangesObj.datasource && onChangesObj.datasource.currentValue && (onChangesObj.datasource.previousValue != onChangesObj.datasource.currentValue)) {
         this.dataset = onChangesObj.datasource.currentValue;
         reload = true;
      }

      if (onChangesObj.datamax && onChangesObj.datamax.currentValue && (onChangesObj.datamax.previousValue != onChangesObj.datamax.currentValue)) {
         this.datamax = onChangesObj.datamax.currentValue;
         reload = true;
      }

      if (onChangesObj.datacurrent && onChangesObj.datacurrent.currentValue && (onChangesObj.datacurrent.previousValue != onChangesObj.datacurrent.currentValue)) {
         this.datacurrent = onChangesObj.datacurrent.currentValue;
         reload = true;
      }

      if (onChangesObj.filter) {
         reload = true;
      }

      if (reload) {
         this.load();
      }
   }

   $postLink() {
      // timeout used to run on next digest cycle so that template will be
      // fully compiled and the id attribute set, required before createChart
      this.$timeout(() => {
         this.chart = this.createChart();
         this.chart.onClick = this.onClick.bind(this);
         this.load();
      });
   }

   private onClick(e: Highcharts.AreaClickEvent) {
      if (this.ds.selectAction) {
         if (this.ds.selectAction.indexOf('set:') == 0) {
            let field = this.ds.selectAction.replace('set:', '');

            // using timeout so that callback is run with angular digest loop
            // without it $onChanges() might not get called for filter updates
            this.$timeout(() => {
               this.onFilterChange({$field: field, $value: e.point.name});
            });
         }
      }
   }

   private createChart() {
      if (this.ds.type == 'pie-chart') {
         return this.pieChartFactory({ds: this.ds});
      } else if (this.ds.type == 'line-chart') {
         return this.lineChartFactory({ds: this.ds});
      } else if (this.ds.type == 'line-chart-custom') {
         return this.lineChartCustomFactory({ds: this.ds});
      } else if (this.ds.type == 'bar-chart-custom') {
         return this.barChartCustomFactory({ds: this.ds});
      } else if (this.ds.type == 'funnel') {
         return this.funnelChartFactory({ds: this.ds});
      } else if (this.ds.type == 'map') {
         return this.mapChartFactory({ds: this.ds});
      } else if (this.ds.type == 'gauge') {
         return this.gaugeChartFactory({ds: this.ds});
      } else if (this.ds.type == 'bar-chart') {
         return this.barChartFactory({ds: this.ds});
      } else if (this.ds.type == 'table-chart') {
         return this.tableChartFactory({ds: this.ds});
      } else if (this.ds.type == 'nps-pie-chart') {
         return this.npsPieChartFactory({ds: this.ds});
      } else if (this.ds.type == 'nps-bar-chart') {
         return this.npsBarChartFactory({ds: this.ds});
      } else if (this.ds.type == 'table-expanded') {
         return this.pieChartGridFactory({ds: this.ds});
      } else {
         throw `unsupported datasource type ${this.ds.type}`;
      }
   }

   load() {
      if (!this.chart || !this.filter) {
         return;
      }

      this.chart.onLoadStart();

      if (this.colors) {
         this.chart.setColors(this.colors);
      }

      if (this.ds.type == 'gauge') {
         let anyOverRides:boolean = false;
         if (this.datamax != undefined && this.datamax != 0) {
            this.chart.setGaugeMinAndMax(0, this.datamax);
            anyOverRides = true;
         }

         if (this.datacurrent != undefined && this.datacurrent != 0) {
            this.chart.setCurrent(this.datacurrent);
            anyOverRides = true;
         }
         if (anyOverRides) {
            this.chart.onLoadFinish();
            return;
         }
      }

      if (this.datamax == undefined && this.datacurrent == undefined) {
         return this.reportsService.data(this.ds.id, this.filter).then((response) => {
            this.chart.setData(response.data);
         }).finally(() => {
            this.chart.onLoadFinish();
         });
      }
      else {
         this.chart.onLoadFinish();
      }
   }
}

ng.module('e360').component('reportChart', {
   template: '<div class="chart-component {{::$ctrl.ds.type}}" id="{{::$ctrl.ds.id}}"></div>',
   controller: ReportChartController,
   bindings: {
      datasource: '<',
      datacurrent: '<',
      datamax: '<',
      filter: '<',
      colors: '<',
      onFilterChange: '&'
   }
});
